import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authAPI, userAPI } from '../services/api';
import { DEFAULT_SMARTCHG_ROLE, SMARTCHG_ROLES, SmartCHGRole, User } from '../types';

// Narrow an arbitrary string from the backend to the User['role'] union, with
// safe fallback. Centralized so every codepath that builds a User stays in sync.
function asUserRole(value: unknown): SmartCHGRole {
  if (typeof value !== 'string') return DEFAULT_SMARTCHG_ROLE;
  return SMARTCHG_ROLES.includes(value as SmartCHGRole)
    ? (value as SmartCHGRole)
    : DEFAULT_SMARTCHG_ROLE;
}
import { tokenService } from '../services/tokenService';
import { logInfo, logWarn, logError, logAuthSuccess, logAuthFailure } from '../utils/logger';
import { getRBACService } from '../services/rbacService';
import { ssoService } from '../services/ssoService';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithSSO: () => Promise<void>;
  completeSSOLogin: (data: SSOLoginResult) => Promise<void>;
  logout: () => void;
  clearAuthData: () => void;
  /** True if the user has the canonical super_admin role (CLAUDE.md §1). */
  isAdmin: () => boolean;
  isUser: () => boolean;
  /** Permission check. Returns true if the user has the named permission OR
   *  is super_admin (role-check short-circuit per CLAUDE.md §2 #11). */
  hasPermission: (permission: string) => boolean;
  /** Module-level access gate. Looks up `<moduleSlug>.access` in the user's
   *  permission list (catalog enumerated in `backend/scripts/seed.py::_MODULE_CATALOG`).
   *  Used by the side-nav to hide modules a user can't reach. Super_admin
   *  short-circuit applies. */
  canAccessModule: (moduleSlug: string) => boolean;
  /** True if the user is admin OR has at least one `<*>.access` permission.
   *  False means the user is logged in but assigned no module — the SPA
   *  should render the "contact your admin" empty state. */
  hasAnyAccess: () => boolean;
  refreshUserProfile: () => Promise<void>;
}

export type SSOLoginResult = {
  token: string;
  refreshToken?: string;
  id: string;
  name?: string;
  email: string;
  role?: string;
  department?: string | null;
  officeLocation?: string | null;
  employeeId?: string | null;
  permissions?: string[];
  isNewUser?: boolean;
  loginHint?: string | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // In-flight guard so concurrent callers (StrictMode double-invoke or auth
  // boot/status checks) share
  // a single GET /api/users/profile round-trip instead of firing two.
  const profileLoadInFlight = useRef<Promise<boolean> | null>(null);

  // Validate token and load user profile
  const validateTokenAndLoadProfile = async (): Promise<boolean> => {
    if (profileLoadInFlight.current) {
      logInfo('Auth', 'validateTokenAndLoadProfile already in flight — returning shared promise');
      return profileLoadInFlight.current;
    }
    logInfo('Auth', 'validateTokenAndLoadProfile called');
    const promise = (async (): Promise<boolean> => {
    try {
      const isValid = await tokenService.validateToken();
      logInfo('Auth', `Token validation result: ${isValid}`);
      
      if (isValid) {
        // Get user profile
        const response = await userAPI.getProfile();
        const profileData = response.data;
        logInfo('Auth', 'Profile data received', profileData);

        const userData: User = {
          id: profileData.id,
          firstName: profileData.firstName || '',
          lastName: profileData.lastName || '',
          email: profileData.email,
          role: asUserRole(profileData.role),
          enabled: profileData.enabled !== false,
          createdAt: profileData.createdAt || new Date().toISOString(),
          updatedAt: profileData.updatedAt || new Date().toISOString(),
          lastLogin: profileData.lastLogin,
          department: profileData.department || undefined,
          officeLocation: profileData.officeLocation || undefined,
          permissions: Array.isArray(profileData.permissions) ? profileData.permissions : undefined,
          employeeId: profileData.employeeId || undefined,
          displayName: profileData.displayName || undefined,
          preferredLanguage: profileData.preferredLanguage || undefined,
          defaultDatasourceId: profileData.defaultDatasourceId || undefined,
          isNewUser: profileData.isNewlyAddedUser === true,
        };

        logInfo('Auth', 'Setting user data', userData);
        setUser(userData);
        tokenService.setUser(userData);
        // Update RBAC service with new user
        getRBACService(userData);
        return true;
      } else {
        logWarn('Auth', 'Token validation failed');
        return false;
      }
    } catch (error: unknown) {
      logWarn('Auth', 'Token validation failed', undefined, error instanceof Error ? error : new Error('Unknown error'));
      return false;
    }
    })();
    profileLoadInFlight.current = promise;
    try {
      return await promise;
    } finally {
      profileLoadInFlight.current = null;
    }
  };

  // ONLY function that should log out users - called when user clicks logout button or token expires
  const logout = () => {
    logInfo('Auth', '🚪 User logged out');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken'); // Clear refresh token too
    tokenService.removeToken();
    setUser(null);
    window.dispatchEvent(new Event('storageChange'));
  };

  // Listen for logout events from API interceptor
  useEffect(() => {
    const handleLogoutEvent = (event: CustomEvent) => {
      logInfo('Auth', 'Received logout event from API interceptor', event.detail);
      logout();
    };

    window.addEventListener('auth:logout', handleLogoutEvent as EventListener);
    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent as EventListener);
    };
  }, []);

  // Check for existing session on mount - logout if token is expired.
  // In dev, React.StrictMode runs effects twice on first render to surface
  // impurity bugs. The auth bootstrap is genuinely a one-shot per page load
  // (it reads from localStorage and fetches the profile); without this guard
  // we'd fire GET /api/users/profile twice on every refresh.
  const authBootstrappedRef = useRef(false);
  useEffect(() => {
    if (authBootstrappedRef.current) return;
    authBootstrappedRef.current = true;
    const checkAuth = async () => {
      logInfo('Auth', 'Starting authentication check');

      // Check for existing token/user in localStorage. The dedicated /callback
      // route (LoginCallback.tsx) writes these before hard-reloading into the app.
      const savedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      logInfo('Auth', `Token exists: ${!!token}`);
      logInfo('Auth', `Saved user exists: ${!!savedUser}`);
      
      // If we have stored auth data, load the full profile before rendering
      // permission-gated navigation.
      if (token && savedUser) {
        logInfo('Auth', 'Found existing authentication data, loading profile for module access...');
        try {
          tokenService.setToken(token, localStorage.getItem('refreshToken') || undefined);
          tokenService.setAxiosAuthHeader(token);
          const profileLoaded = await validateTokenAndLoadProfile();
          if (profileLoaded) {
            logInfo('Auth', 'Profile loaded with permissions for module navigation');
            setIsLoading(false);
            return;
          }
        } catch (error) {
          logWarn('Auth', '⚠️ Profile load failed, using cached user', undefined, error instanceof Error ? error : new Error('Unknown error'));
        }
        try {
          const parsedUser = JSON.parse(savedUser);
          const userData: User = {
            id: parsedUser.id || '',
            firstName: parsedUser.firstName || parsedUser.name?.split(' ')[0] || '',
            lastName: parsedUser.lastName || parsedUser.name?.split(' ').slice(1).join(' ') || '',
            email: parsedUser.email || '',
            role: asUserRole(parsedUser.role),
            enabled: parsedUser.enabled !== false,
            createdAt: parsedUser.createdAt || new Date().toISOString(),
            updatedAt: parsedUser.updatedAt || new Date().toISOString(),
            lastLogin: parsedUser.lastLogin || new Date().toISOString(),
            department: parsedUser.department || undefined,
            permissions: Array.isArray(parsedUser.permissions) ? parsedUser.permissions : undefined,
            employeeId: parsedUser.employeeId || undefined,
            displayName: parsedUser.displayName || undefined,
            preferredLanguage: parsedUser.preferredLanguage || undefined,
            defaultDatasourceId: parsedUser.defaultDatasourceId || undefined,
            isNewUser: typeof parsedUser.isNewUser === 'boolean' ? parsedUser.isNewUser : undefined,
          };
          setUser(userData);
          tokenService.setUser(userData);
          getRBACService(userData);
          window.dispatchEvent(new Event('storageChange'));
          logInfo('Auth', 'User set from cache with permissions if previously loaded');
        } catch (parseError) {
          logError('Auth', 'Failed to parse saved user', undefined, parseError instanceof Error ? parseError : new Error('Unknown error'));
        }
        setIsLoading(false);
        return;
      }

      // If we have a token, check if it's expired
      if (token) {
        // Check if token is expired
        const isExpired = tokenService.isTokenExpired();
        
        if (isExpired) {
          logWarn('Auth', 'Token is expired - attempting refresh before logout');

          // Try to refresh the token. SmartCHG /auth/refresh expects the
          // *refresh* token in the body (not the expired access token); the
          // response shape is { access_token, refresh_token, ... }.
          try {
            const refreshTokenLs = localStorage.getItem('refreshToken');
            if (!refreshTokenLs) {
              logWarn('Auth', 'No refresh token available for expired-token recovery — logging out');
              logout();
              setIsLoading(false);
              return;
            }
            const refreshResponse = await authAPI.refresh(refreshTokenLs);
            const newAccessToken = refreshResponse.data?.access_token;
            const newRefreshToken = refreshResponse.data?.refresh_token;
            if (newAccessToken) {
              logInfo('Auth', 'Token refresh successful on expired token');
              tokenService.setToken(newAccessToken, newRefreshToken);

              // Try to load fresh profile with new token
              const profileLoaded = await validateTokenAndLoadProfile();
              if (profileLoaded) {
                logInfo('Auth', 'Fresh profile loaded with refreshed token');
                setIsLoading(false);
                return;
              }
            }
          } catch (refreshError: unknown) {
            logWarn('Auth', 'Token refresh failed for expired token - logging out user', undefined, refreshError instanceof Error ? refreshError : new Error('Unknown error'));
            // Token is expired and refresh failed - logout user
            logout();
            setIsLoading(false);
            return;
          }
          
          // If refresh didn't return a token, logout
          logWarn('Auth', 'Token refresh did not return new token - logging out user');
          logout();
          setIsLoading(false);
          return;
        }

        // Token is not expired - set token in API headers
        tokenService.setAxiosAuthHeader(token);
        logInfo('Auth', 'Token set in API headers');

        // Try to load fresh profile from API
        try {
          const profileLoaded = await validateTokenAndLoadProfile();
          if (profileLoaded) {
            logInfo('Auth', '✅ Fresh profile loaded successfully from API');
            setIsLoading(false);
            return;
          }
        } catch (error) {
          logWarn('Auth', '⚠️ Failed to load profile from API', undefined, error instanceof Error ? error : new Error('Unknown error'));
          
          // If validation fails, check if token is expired
          if (tokenService.isTokenExpired()) {
            logWarn('Auth', 'Token expired during validation - logging out user');
            logout();
            setIsLoading(false);
            return;
          }
        }

        // If API fails but token is still valid, use saved user data.
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            const fallbackUser: User = {
              id: parsedUser.id || '',
              firstName: parsedUser.firstName || '',
              lastName: parsedUser.lastName || '',
              email: parsedUser.email || '',
              role: asUserRole(parsedUser.role),
              enabled: parsedUser.enabled !== false,
              createdAt: parsedUser.createdAt || new Date().toISOString(),
              updatedAt: parsedUser.updatedAt || new Date().toISOString(),
              lastLogin: parsedUser.lastLogin,
              department: parsedUser.department || undefined,
              permissions: Array.isArray(parsedUser.permissions) ? parsedUser.permissions : undefined,
              employeeId: parsedUser.employeeId || undefined,
              displayName: parsedUser.displayName || undefined,
              preferredLanguage: parsedUser.preferredLanguage || undefined,
              defaultDatasourceId: parsedUser.defaultDatasourceId || undefined,
              isNewUser: typeof parsedUser.isNewUser === 'boolean' ? parsedUser.isNewUser : undefined,
            };
            logInfo('Auth', 'Setting user from localStorage');
            setUser(fallbackUser);
          } catch (parseError: unknown) {
            logWarn('Auth', '⚠️ Failed to parse saved user', undefined, parseError instanceof Error ? parseError : new Error('Unknown error'));
            setUser(null);
          }
        } else {
          logInfo('Auth', '⚠️ No saved user data');
          setUser(null);
        }
      } else {
        logInfo('Auth', 'No token found - user not logged in');
        setUser(null);
      }

      logInfo('Auth', 'Authentication check complete');
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Cross-tab sync disabled to prevent unwanted behavior
  // Each tab maintains its own independent state
  // Auth state only syncs on page load/refresh for security
  // useEffect(() => {
  //   const handleStorageChange = () => {
  //     const savedUser = localStorage.getItem('user');
  //     const token = localStorage.getItem('token');
  //     
  //     if (token) {
  //       tokenService.setAxiosAuthHeader(token);
  //     } else {
  //       tokenService.removeToken();
  //     }

  //     if (savedUser && token) {
  //       try {
  //         const parsedUser = JSON.parse(savedUser);
  //         setUser(parsedUser);
  //       } catch (error: unknown) {
  //         logWarn('Auth', 'Failed to parse saved user', undefined, error instanceof Error ? error : new Error('Unknown error'));
  //         setUser(null);
  //       }
  //     } else {
  //       setUser(null);
  //     }
  //   };

  //   window.addEventListener('storage', handleStorageChange);
  //   window.addEventListener('storageChange', handleStorageChange as EventListener);
  //   return () => {
  //     window.removeEventListener('storage', handleStorageChange);
  //     window.removeEventListener('storageChange', handleStorageChange as EventListener);
  //   };
  // }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      // authAPI.login already adapts /auth/login + /api/users/profile into a
      // frontend auth object. SmartCHG is single-tenant so there is no
      // organizationId to read here (CLAUDE.md §2 #4).
      const {
        token,
        id,
        name,
        email: userEmail,
        role,
        refreshToken,
        department,
        permissions,
        employeeId,
        isNewUser,
      } = response.data;

      const user: User = {
        id,
        firstName: (name || '').split(' ')[0] || '',
        lastName: (name || '').split(' ').slice(1).join(' '),
        email: userEmail,
        role: asUserRole(role),
        enabled: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        department: department || undefined,
        officeLocation: response.data.officeLocation || undefined,
        permissions: Array.isArray(permissions) ? permissions : undefined,
        employeeId: employeeId || undefined,
        isNewUser: isNewUser === true,
      };

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      tokenService.setToken(token, refreshToken);
      tokenService.setSsoLoginHint(null);

      // Update RBAC service with new user
      getRBACService(user);

      window.dispatchEvent(new Event('storageChange'));
      setUser(user);
    } catch (error: any) {
      logError('Auth', 'Login failed', error instanceof Error ? error : new Error('Unknown error'));
      // Re-throw the error so the LoginForm can handle it
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithSSO = async () => {
    setIsLoading(true);
    try {
      logInfo('Auth', 'Initiating Microsoft SSO login');
      // Redirect to Microsoft — callback is handled at /callback (LoginCallback.tsx),
      // which writes token + user to localStorage and hard-reloads so checkAuth picks them up.
      await ssoService.initiateLogin();
    } catch (error: unknown) {
      logError('Auth', 'SSO login failed', error instanceof Error ? error : new Error('Unknown error'));
      setIsLoading(false);
      throw error;
    }
  };

  const completeSSOLogin = async (data: SSOLoginResult) => {
    setIsLoading(true);
    try {
      const fullName = data.name || '';
      const fallbackUser: User = {
        id: data.id,
        firstName: fullName.split(' ')[0] || '',
        lastName: fullName.split(' ').slice(1).join(' '),
        email: data.email,
        role: asUserRole(data.role),
        enabled: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        department: data.department || undefined,
        officeLocation: data.officeLocation || undefined,
        permissions: Array.isArray(data.permissions) ? data.permissions : undefined,
        employeeId: data.employeeId || undefined,
        isNewUser: data.isNewUser === true,
      };

      localStorage.setItem('token', data.token);
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      } else {
        localStorage.removeItem('refreshToken');
      }
      localStorage.setItem('user', JSON.stringify(fallbackUser));
      tokenService.setToken(data.token, data.refreshToken);
      tokenService.setSsoLoginHint(data.loginHint);
      tokenService.setUser(fallbackUser);
      getRBACService(fallbackUser);
      setUser(fallbackUser);

      const profileLoaded = await validateTokenAndLoadProfile();
      if (!profileLoaded) {
        logWarn('Auth', 'SSO profile load failed after callback; using callback user payload');
      }
      window.dispatchEvent(new Event('storageChange'));
    } finally {
      setIsLoading(false);
    }
  };

  // Clear all authentication data (for fixing JWT issues)
  const clearAuthData = () => {
    logInfo('Auth', '🧹 Clearing all authentication data');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    tokenService.removeToken();
    setUser(null);
    window.dispatchEvent(new Event('storageChange'));
  };

  const refreshUserProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      await validateTokenAndLoadProfile();
    } catch (error: unknown) {
      logWarn('Auth', 'Failed to refresh user profile', undefined, error instanceof Error ? error : new Error('Unknown error'));
    }
  };

  // SmartCHG canonical roles are narrowed by asUserRole above.
  const isAdmin = () => user?.role === 'super_admin';
  const isUser = () => !!user && !isAdmin();

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    // super_admin role-check short-circuit (CLAUDE.md §2 #11).
    if (isAdmin()) return true;
    if (Array.isArray(user.permissions) && user.permissions.includes(permission)) {
      return true;
    }
    return false;
  };

  const canAccessModule = (moduleSlug: string): boolean => {
    if (!user) return false;
    // super_admin role-check short-circuit (CLAUDE.md §2 #11).
    if (isAdmin()) return true;
    const slug = String(moduleSlug || '').trim();
    if (!slug) return false;
    if (Array.isArray(user.permissions) && user.permissions.includes(`${slug}.access`)) {
      return true;
    }
    return false;
  };

  const hasAnyAccess = (): boolean => {
    if (!user) return false;
    // Admins always have implicit access (role short-circuit).
    if (isAdmin()) return true;
    if (!Array.isArray(user.permissions)) return false;
    // True if at least one entry follows the `<module>.access` pattern.
    return user.permissions.some((p) => typeof p === 'string' && p.endsWith('.access'));
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      login,
      loginWithSSO,
      completeSSOLogin,
      logout,
      clearAuthData,
      isAdmin,
      isUser,
      hasPermission,
      canAccessModule,
      hasAnyAccess,
      refreshUserProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};
